import {Injectable, SecurityContext} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import { APIURL } from './url.config';
import {Router} from '@angular/router';
import { DomSanitizer} from '@angular/platform-browser';
import { SecureTokenService } from './secure-token.service';
import { SecureIdService } from './secure-id.service';

const LOGIN_ROUTE = '#/login';


/** Service for logging in and obtaining a permission token. */
@Injectable()
export class OauthService {

  expireTime: number;
  private refreshInFlight: Promise<string> | null = null;

  constructor(private http: HttpClient,
              private _router: Router,
              private domSanitizer: DomSanitizer) { }

/**
 *
 */
  getPermissions() {
    return this.http.get<any>(APIURL + 'manage/env')
      .toPromise()
      .then(res => res['applicationConfig: [' +
        (res.commandLineArgs['spring.config.location'] as string) +
        ']']['security.oauth2.client.scope'].replace(',', ' ') as string)
      .catch(this.handleError);
  }

  /**
   * Attempts to login given proper credentials and permissions.
   *
   * @async
   * @func doLogin
   * @param {string} perms - a list of permissions to grant the user.
   */
  doLogin(uName: string, enigmaString: string, perms: string) {
    sessionStorage.setItem('username', uName);

    // UN and PW supplied by user and encoded here. Word Pw encoded for security scans
    const unString = 'username=' + encodeURIComponent(uName);
    const pwString = 'password=' + encodeURIComponent(enigmaString);
    const uaa_body = 'grant_type=password&' + unString + '&' + pwString;

    const uaa_options = {
      //   headers: new HttpHeaders({ Authorization: 'Basic ' + encoded,
      // 'Content-Type': 'application/x-www-form-urlencoded'})
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded'}),
      withCredentials: true
      };

    // Gets the token from the endpoint and saves it in session storage
    return this.http.post<any>(APIURL + 'uaa/oauth/token', uaa_body, uaa_options)
      .toPromise()
      .then(res => {
        SecureTokenService.storeTokens(res.access_token, res.refresh_token, res.expires_in);
        SecureTokenService.clearLogoutMarker();
        SecureIdService.storeTopOrgRootId(res.topOrgRootId);
        // SECURITY: Token stored only in SecureTokenService memory, not plaintext sessionStorage
        // to prevent exposure in developer tools or storage inspection
        sessionStorage.setItem('username', uName); // Store only username, not sensitive tokens

        return res.access_token;
      });

    }

  /**
   * Logs user out by clearing all their data from session storage
   *
   * @async
   * @func doLogout
   */
  doLogout() {
    const currentToken = SecureTokenService.getAccessToken();
    const options = {
      headers: new HttpHeaders({ Authorization: 'Bearer ' + currentToken }),
      withCredentials: true
    };

    // Clear all session state first so any stale browser state cannot be reused.
    sessionStorage.clear();
    SecureTokenService.clearTokens();
    SecureTokenService.markLoggedOut();
    SecureIdService.clearAll();

    try {
      const loginHash = '#/login';
      const currentUrl = window.location.href;
      if (!currentUrl.includes('login')) {
        window.history.replaceState(null, '', `${window.location.origin}${window.location.pathname}${loginHash}`);
      }
    } catch (error) {
      // Ignore browser history issues; session state is already cleared.
    }

    if (currentToken) {
      this.http.delete(APIURL + 'oauth/revoke-token', options)
          .toPromise()
          .then(res => {
            // Do not log token or response containing sensitive data
          })
          .catch(err => {
            // Silently catch errors to avoid exposing token in error logs
          });
    }
    // this.http.get(APIURL + 'exit')
  }

  /**
   * A universally required service that grabs the current authorization token
   * from session storage.
   *
   * @async
   * @func getToken
   * @returns {any} a token object
   */
  getToken() {
     const token = SecureTokenService.getAccessToken();
     if (token) {
       return Promise.resolve(token);
     }

     return this.refreshCurrentSessionToken();
  }

   refreshCurrentSessionToken(): Promise<string> {
     if (SecureTokenService.wasExplicitlyLoggedOut()) {
       return Promise.reject<string>(new Error('Session was explicitly logged out.'));
     }

     if (!this.refreshInFlight) {
       const refreshRequest = sessionStorage.getItem('loginType') === 'sso'
         ? this.refreshSsoToken()
         : this.refreshToken();

       this.refreshInFlight = refreshRequest.finally(() => {
         this.refreshInFlight = null;
       });
     }

     return this.refreshInFlight;
   }
    /**
     * Obtains a new access token using the httpOnly refresh-token cookie.
     *
     * The server sets CAPD_REFRESH_TOKEN as an httpOnly, Secure cookie on every
     * successful login or refresh. Sending no body here forces the server to read
     * the refresh token exclusively from that cookie, so the token is never exposed
     * in the JavaScript context or request body. withCredentials ensures the browser
     * attaches the cookie even when running on the same origin.
     *
     * @async
     * @func refreshToken
     * @returns {Promise<string>} the new access token
     */
    refreshToken() {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded'
            }),
            withCredentials: true
        };

        return this.http.post<any>(APIURL + 'uaa/oauth/refresh_token', null, options)
            .toPromise()
            .then(res => {
                if (SecureTokenService.wasExplicitlyLoggedOut()) {
                    throw new Error('Session was explicitly logged out.');
                }
                SecureTokenService.storeTokens(res.access_token, res.refresh_token, res.expires_in);
                return res.access_token;
            });
    }

    /**
     * Obtains a new access token for SSO sessions using the httpOnly refresh-token
     * cookie set by SsoController. The cookie holds a CoreSTS refresh token, which
     * must be submitted to the CoreSTS endpoint — not UAA.
     */
    refreshSsoToken() {
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded'
            }),
            withCredentials: true
        };

        return this.http.post<any>(APIURL + 'sso/refresh_token', null, options)
            .toPromise()
            .then(res => {
                if (SecureTokenService.wasExplicitlyLoggedOut()) {
                    throw new Error('Session was explicitly logged out.');
                }
                SecureTokenService.storeTokens(res.access_token, res.refresh_token, res.expires_in);
                return res.access_token;
            });
    }

    restoreSessionOnStartup(): Promise<boolean> {
      // If we're on the SSO callback route, SsoLoginComponent will establish the session
      // from the token in the URL. Attempting a refresh here would make a needless network
      // call and — if it succeeds — store tokens that SsoLoginComponent immediately overwrites
      // (discarding the refresh token and expiry in the process).
      if (window.location.href.includes('/SsoLogin/')) {
        return Promise.resolve(true);
      }

      if (SecureTokenService.wasExplicitlyLoggedOut()) {
        SecureTokenService.clearTokens();
        SecureIdService.clearAll();
        return Promise.resolve(false);
      }

      const currentToken = SecureTokenService.getAccessToken();
      if (currentToken) {
        return Promise.resolve(true);
      }

      // SSO sessions use a CoreSTS refresh token; regular logins use a UAA refresh token.
      // Route to the correct refresh endpoint based on how the user originally logged in.
      return this.refreshCurrentSessionToken()
        .then(() => true)
        .catch(() => false);
    }

    loginWithSso() {
        this.http.get(APIURL + 'sso/authorize', {responseType: 'text' as const}).subscribe(res => {
            const url = this.domSanitizer.sanitize(SecurityContext.URL, res);
            window.location.href = new URL(url).href;
        });
    }

    getUIVersion() {
        return this.http.get(APIURL + 'uiVersion')
            .toPromise();
    }

  /** Handles errors */
  private handleError(error: Response) {
    console.error(error);
    return 'read package-management';
  }
}
